import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterEvent } from '@angular/router';
import { AssetPreviewPipe } from 'src/app/shared/pipes/asset-preview.pipe';
import { HomePageComponentImports } from '../..';
import { GetCollectionQuery, GetCollectionQueryVariables, GetCollectionsQuery } from '../../../common/generated-types';
import { DataService } from '../../providers/data/data.service';
import { StateService } from '../../providers/state/state.service';
import { SeoService } from '../../seo/seo.service';
import { GET_COLLECTION } from '../product-list/product-list.graphql';

@Component({
    standalone: true,
    imports: HomePageComponentImports,
    selector: 'vsf-home-page',
    templateUrl: './home-page.component.html',
    styleUrls: ['./home-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomePageComponent implements OnInit {
    private dataService = inject(DataService);
    private breakpointObserver = inject(BreakpointObserver);
    private router = inject(Router);
    private sanitizer = inject(DomSanitizer);
    private route = inject(ActivatedRoute);
    private stateService = inject(StateService);
    private seoService = inject(SeoService);
    private assetPreviewPipe = new AssetPreviewPipe();

    breadcrumbs$: Observable<Array<{ id: string; name: string }>>;
    collection$: Observable<GetCollectionQuery['collection']>;
    isHomePage$: Observable<boolean>;
    collections$: Observable<(GetCollectionsQuery['collections']['items'][number] & { safeImageUrl?: SafeUrl })[]>;
    isMobile$: Observable<boolean>;
    private currentPage = 0;

    ngOnInit(): void {
        this.seoService.setDefault();
        this.isMobile$ = this.breakpointObserver.observe([`(max-width: 1023px)`]).pipe(map((result) => result.matches));
        this.isHomePage$ = this.router.events.pipe(
            filter<any>((event) => event instanceof RouterEvent),
            map((event: RouterEvent) => event.url === '/')
        );
        this.collections$ = this.dataService
            .query<GetCollectionsQuery>(GET_COLLECTIONS, {
                options: { take: 50 }
            })
            .pipe(
                map(({ collections }) =>
                    collections.items.map((c) => {
                        // console.log(c);
                        return {
                            ...c,
                            safeImageUrl: c.featuredAsset
                                ? this.sanitizer.bypassSecurityTrustUrl(this.assetPreviewPipe.transform(c.featuredAsset, 550, 400))
                                : 'assets/imgs/no-image.png'
                        };
                    })
                )
            );
        const collectionSlug$ = this.route.paramMap.pipe(
            map((pm) => pm.get('slug')),
            distinctUntilChanged(),
            tap((slug) => {
                this.stateService.setState('lastCollectionSlug', slug || null);
                this.currentPage = 0;
                // console.log(slug);
            }),
            shareReplay(1)
        );
        this.collection$ = collectionSlug$.pipe(
            switchMap((slug) => {
                if (slug) {
                    return this.dataService
                        .query<GetCollectionQuery, GetCollectionQueryVariables>(GET_COLLECTION, {
                            slug
                        })
                        .pipe(map((data) => data.collection));
                } else {
                    return of(undefined);
                }
            }),
            shareReplay(1)
        );
        this.breadcrumbs$ = this.collection$.pipe(
            map((collection) => {
                // console.log(collection);
                if (collection) {
                    return collection.breadcrumbs;
                } else {
                    return [
                        {
                            id: '',
                            name: 'Home'
                        }
                        // {
                        //     id: '',
                        //     name: 'Search',
                        // }
                    ];
                }
            })
        );
    }
}

const GET_COLLECTIONS = gql`
    query GetCollections($options: CollectionListOptions) {
        collections(options: $options) {
            items {
                id
                name
                slug
                parent {
                    id
                    slug
                    name
                }
                featuredAsset {
                    id
                    preview
                }
            }
        }
    }
`;
